import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useState, useRef, useCallback } from 'react';
import { IconSpinnerSolid, IconPencilOutline, IconTrashOutline } from '@circlefin/euclid-icons/common';
import classNames from 'classnames';
import { Button } from '../Button/Button.es.js';
import { Card } from '../Card/Card.es.js';
import { FileUploaderItem } from './FileUploader.Item/FileUploader.Item.es.js';

/**
 * Keys passed when an error occurs.
 */ var ErrorType;
(function(ErrorType) {
    ErrorType["ERROR_MAX_SIZE"] = "maxSizeExceeded";
    ErrorType["ERROR_FILE_TYPE"] = "fileTypeNotSupported";
})(ErrorType || (ErrorType = {}));
/**
 * Constants.
 */ const ONE_MEGABYTE = 1000000;
const MAX_ALLOWED_SIZE = 1000000000;
const FileUploader = ({ name, imageUrl = null, size = 'base', className, children, fileTypes = [], maxSize = ONE_MEGABYTE, allowMultiple = false, loading = false, warning = false, disabled = false, onDragOver, onLeave, onFileSelected, onFilesSelected, onError, ...props })=>{
    const [draggingFile, setDraggingFile] = useState(false);
    if (maxSize > MAX_ALLOWED_SIZE) {
        throw new Error(`Max allowed size is ${MAX_ALLOWED_SIZE} bytes`);
    }
    /**
     * Hooks.
     */ const inputRef = useRef(null);
    /**
     * Check whether a given file meets certain conditions.
     * @param File - The file to be validated.
     * @param e - The event triggered by the file input change or file drop.
     * @returns Boolean Return true if the file passes all checks, and false otherwise.
     *
     * This function checks:
     * - If the type of the file is included in the 'accept' attribute of the input element (if specific file types are required).
     * - If the size of the file exceeds the maximum allowed size.
     *
     * If the file fails any of the checks, the onError callback will be invoked.
     */ const isFileValid = useCallback((file, e)=>{
        const input = e.target;
        const allowedTypes = new Set(input.accept.split(','));
        const isFileTypeInvalid = !allowedTypes.has(file.type) && fileTypes.length > 0;
        const isFileSizeExceed = file.size > maxSize;
        if (isFileTypeInvalid) {
            onError?.({
                type: ErrorType.ERROR_FILE_TYPE,
                fileTypes: fileTypes
            });
            return false;
        }
        if (isFileSizeExceed) {
            onError?.({
                type: ErrorType.ERROR_MAX_SIZE,
                maxSize: maxSize
            });
            return false;
        }
        return true;
    }, [
        fileTypes,
        maxSize,
        onError
    ]);
    /**
     * Handles the file input change event. Triggers the appropriate callbacks after validating the selected file(s).
     * @param e - React.ChangeEvent<HTMLInputElement> - The event object from the file input change.
     *
     * The function performs the following tasks:
     * - Exits if no files are selected.
     * - Converts FileList from the input event to an array and validates each file. Exits if any file fails validation.
     * - If only a single file is allowed and all files pass validation, triggers the onFileSelected callback with the first file.
     * - If multiple files are allowed and all files pass validation, triggers the onFilesSelected callback with all files.
     */ const handleFileInput = useCallback((e)=>{
        if (!e.target?.files || e.target.files.length === 0) {
            return;
        }
        const files = Array.from(e.target.files);
        if (files.some((file)=>!isFileValid(file, e))) {
            // Reset the file input value, otherwise the onChange callback will not be triggered if we select the same file.
            e.target.value = '';
            return;
        }
        if (allowMultiple) {
            onFilesSelected?.(files);
        } else {
            onFileSelected?.(files[0]);
        }
        // Reset the file input value after the file(s) have been processed.
        // Otherwise the onChange callback will not be triggered if we select the same file.
        e.target.value = '';
    }, [
        allowMultiple,
        isFileValid,
        onFileSelected,
        onFilesSelected
    ]);
    /**
     * Check file type and size.
     */ const handleDrop = useCallback((e)=>{
        const files = Array.from(e.dataTransfer.files);
        if (files.some((file)=>!isFileValid(file, e))) {
            e.preventDefault();
        }
        setDraggingFile(false);
    }, [
        isFileValid
    ]);
    /**
     * Remove selected file.
     */ const handleRemoveImage = useCallback(()=>{
        onFileSelected?.(null);
        // Reset the file input value after the file(s) have been removed.
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    }, [
        onFileSelected
    ]);
    /**
     * Open file dialog.
     */ const handleChange = useCallback(()=>{
        inputRef.current?.click();
    }, []);
    const handleDragOver = useCallback((e)=>{
        const draggingFile = e.dataTransfer.types?.some((t)=>t === 'Files');
        setDraggingFile(draggingFile);
        onDragOver?.({
            draggingFile
        });
    }, [
        onDragOver
    ]);
    const handleLeave = useCallback(()=>{
        setDraggingFile(false);
        onLeave?.();
    }, [
        onLeave
    ]);
    return jsxs("div", {
        className: classNames('cb-file-uploader', size, className),
        ...props,
        children: [
            jsx(Card, {
                className: classNames('input-container', {
                    'input-bg': !imageUrl && !loading && !warning,
                    'dragging-file': draggingFile,
                    warning,
                    loading
                }),
                "data-testid": "fileuploader-card",
                disabled: disabled,
                variant: "dashed/base",
                children: loading ? jsx("div", {
                    className: "spinner",
                    children: jsx(IconSpinnerSolid, {
                        className: "animate-spin"
                    })
                }) : jsxs(Fragment, {
                    children: [
                        imageUrl && jsx("img", {
                            alt: "preview",
                            className: "preview",
                            src: imageUrl
                        }),
                        jsx("input", {
                            ref: inputRef,
                            accept: fileTypes.join(','),
                            className: "input",
                            "data-testid": "file-input",
                            disabled: disabled,
                            multiple: allowMultiple,
                            name: name,
                            onChange: handleFileInput,
                            onDragLeave: handleLeave,
                            onDragOver: handleDragOver,
                            onDrop: handleDrop,
                            onMouseLeave: handleLeave,
                            type: "file"
                        }),
                        !imageUrl && jsx("div", {
                            className: "message",
                            children: children
                        })
                    ]
                })
            }),
            imageUrl && jsxs("div", {
                className: "buttons-container",
                children: [
                    jsxs(Button, {
                        className: "button",
                        disabled: disabled,
                        onClick: handleChange,
                        variant: "secondary",
                        children: [
                            jsx(IconPencilOutline, {
                                size: 16
                            }),
                            jsx("p", {
                                className: "button-text",
                                children: "Change"
                            })
                        ]
                    }),
                    jsxs(Button, {
                        className: "button",
                        disabled: disabled,
                        onClick: handleRemoveImage,
                        variant: "secondary",
                        children: [
                            jsx(IconTrashOutline, {
                                size: 16
                            }),
                            jsx("p", {
                                className: "button-text",
                                children: "Remove"
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
FileUploader.displayName = 'FileUploader';
FileUploader.Item = FileUploaderItem;

export { ErrorType, FileUploader, MAX_ALLOWED_SIZE, ONE_MEGABYTE, FileUploader as default };
//# sourceMappingURL=FileUploader.es.js.map
